// Format-/Label-Helfer Einkaufsmodul (W4.1). Anzeige de-DE; CSV bleibt Punkt-Dezimal (lib/csv.ts).
export function formatUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

const ORDER_LABELS: Record<string, string> = {
  entwurf: "Entwurf", bestellt: "Bestellt", in_abwicklung: "In Abwicklung",
  abgeschlossen: "Abgeschlossen", storniert: "Storniert",
};
const SHIPMENT_LABELS: Record<string, string> = {
  produktion: "Produktion", abholbereit: "Abholbereit", unterwegs: "Unterwegs",
  zugestellt: "Zugestellt", im_wareneingang: "Im Wareneingang",
  eingelagert: "Eingelagert", geparkt: "Geparkt",
};
const PAYMENT_LABELS: Record<string, string> = {
  offen: "Offen", teilbezahlt: "Teilbezahlt", bezahlt: "Bezahlt",
};
const DOC_STATUS_LABELS: Record<string, string> = {
  neu: "Neu",
  extrahiert: "Wartet auf Review",
  bestaetigt: "Bestätigt",
  abgelehnt: "Abgelehnt",
  // 0156: Upload-Option „Nur ablegen (ohne Auswertung)“ — Endstatus, kein Review.
  abgelegt: "Abgelegt",
};
// W4.3 WE-Bruecke: Status des vor-belegten Wareneingangs (warehouse_receipts.status,
// notNull default 'offen'). Werte gespiegelt aus src/shared/db/schema/warehouse.ts.
const WE_STATUS_LABELS: Record<string, string> = {
  offen: "Offen", teilweise: "Teilweise",
  vollstaendig: "Vollständig", abgeschlossen: "Abgeschlossen",
};
// Statusfarben (Plan W4.3): offen zinc · teilweise amber · vollstaendig sky · abgeschlossen green.
// Gleiches Badge-Schema wie die Dokument-Status-Badges (bg-100/text-800 · dark bg-900/40/text-300).
const WE_STATUS_BADGE: Record<string, string> = {
  offen: "bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200",
  teilweise: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  vollstaendig: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  abgeschlossen: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
};
const DOC_TYP_LABELS: Record<string, string> = {
  commercial_invoice: "Handelsrechnung",
  packing_list: "Packliste",
  invoice_packing_combo: "Rechnung+Packliste",
  hbl: "B/L",
  zollbeleg: "Zollbeleg",
  spediteur_rechnung: "Spediteur-Rechnung",
  status_report: "Statusreport",
  sonstig: "Sonstig",
  nicht_parsebar: "Nicht lesbar",
};

export const orderStatusLabel = (s: string): string => ORDER_LABELS[s] ?? s;
export const shipmentStatusLabel = (s: string): string => SHIPMENT_LABELS[s] ?? s;
export const paymentStatusLabel = (s: string): string => PAYMENT_LABELS[s] ?? s;
export const docStatusLabel = (s: string): string => DOC_STATUS_LABELS[s] ?? s;
export const docTypLabel = (s: string): string => DOC_TYP_LABELS[s] ?? s;
export const weStatusLabel = (s: string): string => WE_STATUS_LABELS[s] ?? s;
export const weStatusBadgeClass = (s: string | null | undefined): string =>
  (s ? WE_STATUS_BADGE[s] : undefined) ?? WE_STATUS_BADGE.offen;

export const ORDER_STATUS_OPTIONS = ["entwurf", "bestellt", "in_abwicklung", "abgeschlossen", "storniert"] as const;
export const SHIPMENT_STATUS_OPTIONS =
  ["produktion", "abholbereit", "unterwegs", "zugestellt", "im_wareneingang", "eingelagert", "geparkt"] as const;

export function pushErgebnisLabel(
  item: { ok: boolean; valueBefore?: number | null; error?: string },
  pushedQty?: number
): string {
  if (item.ok) {
    if (item.valueBefore !== null && item.valueBefore !== undefined) {
      return `vorher ${item.valueBefore} → gepusht ${pushedQty ?? 0}`;
    }
    return `gepusht ${pushedQty ?? 0}`;
  }
  return item.error ?? "Fehler beim Push";
}

/** Ausgang eines Mehrfach-Uploads in der Akte — jede Datei zaehlt genau einmal. */
export interface UploadBilanz {
  gespeichert: number;
  /** Teilmenge von `gespeichert`: verarbeitet, aber das Original liegt NICHT im SharePoint. */
  ohneSharepoint: number;
  /** Teilmenge von `gespeichert`: gespeichert, aber nicht automatisch ausgewertet (z. B. KI-Ausfall). */
  ohneAuswertung: number;
  vorhanden: number;
  fehlgeschlagen: number;
}

export function uploadZusammenfassung(bilanz: UploadBilanz, nurAblegen: boolean): string {
  const gesamt = bilanz.gespeichert + bilanz.vorhanden + bilanz.fehlgeschlagen;
  const hinweise: string[] = [];
  if (bilanz.ohneSharepoint > 0) hinweise.push(`${bilanz.ohneSharepoint} ohne SharePoint-Ablage`);
  if (bilanz.ohneAuswertung > 0) hinweise.push(`${bilanz.ohneAuswertung} ohne Auswertung`);
  const zusatz = hinweise.length > 0 ? ` (${hinweise.join(", ")})` : "";
  const teile = [`${bilanz.gespeichert} ${nurAblegen ? "abgelegt" : "hochgeladen"}${zusatz}`];
  if (bilanz.vorhanden > 0) teile.push(`${bilanz.vorhanden} bereits vorhanden`);
  if (bilanz.fehlgeschlagen > 0) teile.push(`${bilanz.fehlgeschlagen} fehlgeschlagen`);
  return `${gesamt} Dateien: ${teile.join(", ")}`;
}

/**
 * Meldung fuer ein gespeichertes, aber NICHT ausgewertetes Dokument; `null` fuer alle anderen
 * Ausgaenge. Kein Fehler: die Datei liegt in der Akte und laesst sich ablegen.
 */
export function uploadOhneAuswertungText(outcome: string, grund: string | undefined): string | null {
  if (outcome === "stored_unparsed") {
    return "Dokument gespeichert — Inhalt nicht lesbar, keine automatische Auswertung. In der Liste „Ablegen“ wählen.";
  }
  if (outcome !== "stored_unextracted") return null;
  if (grund === "ki_nicht_konfiguriert") {
    return "Dokument gespeichert — automatische Auswertung ist nicht eingerichtet. In der Liste „Ablegen“ wählen.";
  }
  return "Dokument gespeichert — automatische Auswertung fehlgeschlagen (KI-Dienst nicht erreichbar). Später erneut hochladen oder in der Liste „Ablegen“ wählen.";
}

export function mischEkHinweis(altEkFehlt: boolean): string {
  return altEkFehlt ? "Kein Alt-EK — Misch-EK = Landed-EK" : "";
}

export function dokumentLoeschHinweis(sharepointPath: string | null): string {
  const archivHinweis = sharepointPath
    ? "Die Originaldatei bleibt im SharePoint erhalten."
    : "Für dieses Dokument ist keine SharePoint-Ablage hinterlegt.";
  return [
    "Bitte gib einen Grund für das Löschen an. Das Dokument wird aus der Plattform entfernt.",
    archivHinweis,
    "Bereits übernommene Bestell- oder Sendungspositionen bleiben unverändert.",
    "Ein automatisches Wiedereinlesen derselben unveränderten SharePoint-Datei wird verhindert; über einen bewussten manuellen Upload kann sie erneut verarbeitet werden.",
  ].join(" ");
}
